import { useEffect, useState, useRef, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { X, ImageIcon, FolderOpen, Upload } from 'lucide-react'
import { Dialog as LibraryDialog, DialogContent as LibraryDialogContent, DialogHeader as LibraryDialogHeader, DialogTitle as LibraryDialogTitle } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useCreateLocation, useUpdateLocation, useLocationCategories, useLocationCountries } from '../hooks'
import { createLocationSchema, updateLocationSchema, type LocationFormData } from '../schema'
import { buildAppImageUrl } from '@/lib/imagePaths'
import { WorldLocationSearch } from './WorldLocationSearch'
import type { Location } from '@/types/entities'
import { useAuthStore } from '@/app/store/auth'

interface LocationFormDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    location: Location | null
}

export function LocationFormDialog({ open, onOpenChange, location }: LocationFormDialogProps) {
    const { t } = useTranslation()
    const isEditing = !!location
    const createLocation = useCreateLocation()
    const updateLocation = useUpdateLocation()
    // Only fetch categories when dialog is open to avoid unnecessary API calls
    const { data: categories = [], isLoading: categoriesLoading } = useLocationCategories(open)

    const [isUploading, setIsUploading] = useState(false)
    const [showLibrary, setShowLibrary] = useState(false)
    const [libraryImages, setLibraryImages] = useState<{ filename: string; url: string }[]>([])
    const [mapCoords, setMapCoords] = useState<{ lat: number; lng: number } | null>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)

    const loadLibrary = useCallback(async () => {
        try {
            const token = useAuthStore.getState().accessToken
            const res = await fetch('/api/locations/images/library', {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {},
            })
            if (res.ok) {
                const data = await res.json()
                setLibraryImages(data.data || [])
            }
        } catch { /* ignore */ }
    }, [])

    const form = useForm<LocationFormData>({
        resolver: zodResolver(isEditing ? updateLocationSchema : createLocationSchema) as any,
        mode: 'onChange',
        defaultValues: {
            description: '',
            address1: '',
            address2: '',
            county: '',
            postcode: '',
            categoryId: null,
            locationId: null,
            imageFilename: null,
            scope: 'city',
            countryCode: null,
        },
    })
    // A city has its city (coordinates, time zone); a country its country; a region only its name.
    const scope = form.watch('scope') ?? 'city'
    const { data: countries = [] } = useLocationCountries(open && scope === 'country')

    // Reset form when dialog opens/closes or location changes
    useEffect(() => {
        if (open) {
            if (location) {
                form.reset({
                    description: location.description || '',
                    address1: location.address1 || '',
                    address2: location.address2 || '',
                    county: location.county || '',
                    postcode: location.postcode || '',
                    categoryId: location.categoryId || null,
                    locationId: location.locationId || null,
                    imageFilename: location.imageFilename || null,
                    scope: location.scope || 'city',
                    countryCode: location.countryCode || null,
                })
            } else {
                form.reset({
                    description: '',
                    address1: '',
                    address2: '',
                    county: '',
                    postcode: '',
                    categoryId: null,
                    locationId: null,
                    imageFilename: null,
                    scope: 'city',
                    countryCode: null,
                })
            }
        }
    }, [open, location, form])

    const handleSubmit = async (data: LocationFormData) => {
        try {
            if (isEditing && location) {
                await updateLocation.mutateAsync({
                    id: location.id,
                    data,
                })
            } else {
                await createLocation.mutateAsync(data)
            }
            onOpenChange(false)
        } catch (error) {
            console.error('Form submit error:', error)
        }
    }

    const isLoading = createLocation.isPending || updateLocation.isPending

    return (
        <>
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[750px]">
                <DialogHeader>
                    <DialogTitle>
                        {isEditing ? t('locations.editLocation') : t('locations.createLocation')}
                    </DialogTitle>
                    <DialogDescription>
                        {isEditing
                            ? t('locations.updateLocationInfo')
                            : t('locations.createNewLocationDescription')}
                    </DialogDescription>
                </DialogHeader>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(handleSubmit as any)} className="space-y-4">
                      <div className="grid grid-cols-2 gap-6">
                        {/* Left column — address details */}
                        <div className="space-y-4">
                        <FormField
                            control={form.control}
                            name="description"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t('locations.description')}</FormLabel>
                                    <FormControl>
                                        <Input {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="address1"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t('locations.address1')}</FormLabel>
                                    <FormControl>
                                        <Input {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="address2"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t('locations.address2')}</FormLabel>
                                    <FormControl>
                                        <Input {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="county"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t('locations.county')}</FormLabel>
                                    <FormControl>
                                        <Input {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="postcode"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t('locations.postcode')}</FormLabel>
                                    <FormControl>
                                        <Input {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="categoryId"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t('locations.category')}</FormLabel>
                                    <Select
                                        onValueChange={(value) => field.onChange(value ? Number(value) : null)}
                                        value={field.value?.toString() || ''}
                                    >
                                        <FormControl>
                                            <SelectTrigger>
                                                <SelectValue placeholder={t('locations.selectCategory')} />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            {Array.isArray(categories) && categories.map((category) => (
                                                <SelectItem key={category.id} value={category.id.toString()}>
                                                    {category.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        </div>

                        {/* Right column — world location + image */}
                        <div className="space-y-4">
                        <FormField
                            control={form.control}
                            name="scope"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t('locations.kind', 'Kind')}</FormLabel>
                                    <Select onValueChange={(value) => { field.onChange(value); if (value !== 'city') setMapCoords(null) }} value={field.value ?? 'city'}>
                                        <FormControl>
                                            <SelectTrigger data-testid="location-kind"><SelectValue /></SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            <SelectItem value="city">{t('locations.kindCity', 'City')}</SelectItem>
                                            <SelectItem value="country">{t('locations.kindCountry', 'Country')}</SelectItem>
                                            <SelectItem value="region">{t('locations.kindRegion', 'Geographic region')}</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    {scope !== 'city' && (
                                        <p className="text-xs text-muted-foreground" data-testid="location-kind-hint">
                                            {scope === 'country'
                                                ? t('locations.kindCountryHint', 'A country has no coordinates or time zone. Use it when only the country is known, such as a SaaS service held "in the United Kingdom".')
                                                : t('locations.kindRegionHint', 'A region is named by its description, such as "European Union" or "Asia Pacific". It has no coordinates or time zone.')}
                                        </p>
                                    )}
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        {scope === 'country' && (
                            <FormField
                                control={form.control}
                                name="countryCode"
                                render={({ field, fieldState }) => (
                                    <FormItem>
                                        <FormLabel>
                                            {t('locations.country')}
                                            <span className="ml-0.5 text-destructive" aria-hidden>*</span>
                                        </FormLabel>
                                        <Select onValueChange={field.onChange} value={field.value ?? ''}>
                                            <FormControl>
                                                <SelectTrigger data-testid="location-country"><SelectValue placeholder={t('locations.chooseCountry', 'Choose the country')} /></SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                {countries.map((c) => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                        {fieldState.error && <p className="text-sm font-medium text-destructive" role="alert">{t('locations.countryRequired', 'Choose the country.')}</p>}
                                    </FormItem>
                                )}
                            />
                        )}

                        {scope === 'city' && (
                        <FormField
                            control={form.control}
                            name="locationId"
                            render={({ field, fieldState }) => (
                                <FormItem>
                                    <FormLabel>
                                        {t('locations.worldLocation')}
                                        <span className="ml-0.5 text-destructive" aria-hidden>*</span>
                                    </FormLabel>
                                    <FormControl>
                                        <WorldLocationSearch
                                            value={field.value || null}
                                            onValueChange={field.onChange}
                                            onLocationSelect={(loc) => {
                                                if (loc?.lat != null && loc?.lng != null) {
                                                    setMapCoords({ lat: loc.lat, lng: loc.lng })
                                                } else {
                                                    setMapCoords(null)
                                                }
                                            }}
                                        />
                                    </FormControl>
                                    {/* Always says why it is asked for; turns into the error when it is missing. */}
                                    <p
                                        className={fieldState.error ? 'text-sm font-medium text-destructive' : 'text-xs text-muted-foreground'}
                                        role={fieldState.error ? 'alert' : undefined}
                                        data-testid="world-location-hint"
                                    >
                                        {fieldState.error
                                            ? t('locations.worldLocationRequired', 'Choose the city this location is in.')
                                            : t('locations.worldLocationHint', 'Gives the location its coordinates and time zone.')}
                                    </p>
                                </FormItem>
                            )}
                        />
                        )}

                        {/* Google Maps preview */}
                        {scope === 'city' && mapCoords && (
                            <div className="rounded-lg overflow-hidden border" style={{ height: '160px' }}>
                                <iframe
                                    title="Location map"
                                    src={`https://www.google.com/maps?q=${mapCoords.lat},${mapCoords.lng}&z=13&output=embed`}
                                    width="100%"
                                    height="100%"
                                    style={{ border: 0 }}
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                />
                            </div>
                        )}

                        {/* Image upload — floor plan or cloud diagram */}
                        <FormField
                            control={form.control}
                            name="imageFilename"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t('locations.image', 'Floor Plan / Diagram')}</FormLabel>
                                    <FormControl>
                                        <div className="space-y-2">
                                            {field.value ? (
                                                <div className="relative rounded-md border overflow-hidden">
                                                    <img
                                                        src={buildAppImageUrl('locations', field.value ?? '')}
                                                        alt="Location diagram"
                                                        className="w-full max-h-[200px] object-contain bg-muted/30"
                                                    />
                                                    <Button
                                                        type="button"
                                                        variant="destructive"
                                                        size="sm"
                                                        className="absolute top-2 right-2 h-7 w-7 p-0"
                                                        onClick={() => field.onChange(null)}
                                                    >
                                                        <X className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            ) : (
                                                <div className="flex gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => fileInputRef.current?.click()}
                                                        disabled={isUploading}
                                                        className="flex-1 flex items-center justify-center gap-2 rounded-md border-2 border-dashed border-muted-foreground/25 hover:border-muted-foreground/50 py-5 text-sm text-muted-foreground transition-colors"
                                                    >
                                                        {isUploading ? (
                                                            <span>Uploading...</span>
                                                        ) : (
                                                            <>
                                                                <Upload className="h-4 w-4" />
                                                                <span>Upload</span>
                                                            </>
                                                        )}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => { loadLibrary(); setShowLibrary(true) }}
                                                        className="flex-1 flex items-center justify-center gap-2 rounded-md border-2 border-dashed border-muted-foreground/25 hover:border-muted-foreground/50 py-5 text-sm text-muted-foreground transition-colors"
                                                    >
                                                        <FolderOpen className="h-4 w-4" />
                                                        <span>Library</span>
                                                    </button>
                                                </div>
                                            )}
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                accept="image/*"
                                                className="hidden"
                                                onChange={async (e) => {
                                                    const file = e.target.files?.[0]
                                                    if (!file) return
                                                    setIsUploading(true)
                                                    try {
                                                        const formData = new FormData()
                                                        formData.append('image', file)
                                                        const token = useAuthStore.getState().accessToken
                                                        const res = await fetch('/api/upload?subdirectory=locations', {
                                                            method: 'POST',
                                                            headers: token ? { 'Authorization': `Bearer ${token}` } : {},
                                                            body: formData,
                                                        })
                                                        if (res.ok) {
                                                            const data = await res.json()
                                                            field.onChange(data.image.filename)
                                                        }
                                                    } catch (err) {
                                                        console.error('Upload failed:', err)
                                                    } finally {
                                                        setIsUploading(false)
                                                        if (fileInputRef.current) fileInputRef.current.value = ''
                                                    }
                                                }}
                                            />
                                        </div>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        </div>
                      </div>

                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                                disabled={isLoading}
                            >
                                {t('common.cancel')}
                            </Button>
                            <Button type="submit" disabled={isLoading}>
                                {isLoading
                                    ? isEditing
                                        ? t('locations.updating')
                                        : t('locations.creating')
                                    : isEditing
                                      ? t('locations.update')
                                      : t('locations.create')}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>

        {/* Image Library Picker */}
        <LibraryDialog open={showLibrary} onOpenChange={setShowLibrary}>
            <LibraryDialogContent className="sm:max-w-[600px] max-h-[80vh]">
                <LibraryDialogHeader>
                    <LibraryDialogTitle>Select from Library</LibraryDialogTitle>
                </LibraryDialogHeader>
                {libraryImages.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-8 text-center">
                        No images in the library yet. Upload an image first.
                    </p>
                ) : (
                    <div className="grid grid-cols-3 gap-3 overflow-y-auto max-h-[50vh] p-1">
                        {libraryImages.map((img) => (
                            <button
                                key={img.filename}
                                type="button"
                                onClick={() => {
                                    form.setValue('imageFilename', img.filename, { shouldDirty: true })
                                    setShowLibrary(false)
                                }}
                                className="rounded-md border-2 border-transparent hover:border-primary overflow-hidden transition-colors"
                            >
                                <img
                                    src={img.url}
                                    alt={img.filename}
                                    className="w-full h-24 object-cover bg-muted/30"
                                />
                                <p className="text-xs text-muted-foreground truncate px-1 py-0.5">{img.filename}</p>
                            </button>
                        ))}
                    </div>
                )}
            </LibraryDialogContent>
        </LibraryDialog>
        </>
    )
}

